import { useEffect, useState } from "react";
import ToastHelper from "@/utils/toastHelper";
import apiClient from "@/services/apiClient";
import { LifeBuoy, Plus, X, Send, MessageSquare, ChevronRight, Search, Paperclip, Clock, CheckCircle2, AlertCircle } from "lucide-react";

const CATEGORIES = ["Enrolment", "Payment", "Certificate", "Technical", "USI / ID", "Other"];
const PRIORITIES = ["Low", "Normal", "High", "Urgent"];

const INITIAL_TICKETS = [
  {
    id: "T-2081",
    subject: "Cannot download my Statement of Attainment",
    category: "Certificate",
    priority: "High",
    status: "Open",
    created: "2026-02-08T09:14:00Z",
    lastReply: "1 hour ago",
    unread: true,
    messages: [
      { from: "you", body: "Hi team, I tried downloading my SoA from the Certificates page but I get a 404 error. Booking ID B-8712.", at: "2026-02-08T09:14:00Z" },
      { from: "support", body: "Hey Daniel - thanks for flagging. I've regenerated the PDF and it should now be available. Please try again and let us know.", at: "2026-02-08T10:02:00Z", agent: "Sam (Support)" },
    ],
  },
  {
    id: "T-2079",
    subject: "Request refund for cancelled class",
    category: "Payment",
    priority: "Normal",
    status: "In review",
    created: "2026-02-05T14:30:00Z",
    lastReply: "yesterday",
    unread: false,
    messages: [
      { from: "you", body: "I had to cancel my 5 Feb booking. Could you please process a refund? Receipt R-3006.", at: "2026-02-05T14:30:00Z" },
      { from: "support", body: "Confirmed - refund submitted to your card ending 3333. Please allow 3-5 business days.", at: "2026-02-06T11:45:00Z", agent: "Nadia (Support)" },
    ],
  },
  {
    id: "T-2064",
    subject: "USI verification not working",
    category: "USI / ID",
    priority: "Normal",
    status: "Resolved",
    created: "2026-01-27T08:00:00Z",
    lastReply: "1 week ago",
    unread: false,
    messages: [
      { from: "you", body: "I entered my USI but it says invalid. Can you help?", at: "2026-01-27T08:00:00Z" },
      { from: "support", body: "The issue was a typo - I've corrected it on our side. You're all set!", at: "2026-01-27T09:22:00Z", agent: "Sam (Support)" },
      { from: "you", body: "Thanks!", at: "2026-01-27T09:30:00Z" },
    ],
  },
];

const STATUS_STYLE = {
  Open: "bg-amber-50 text-amber-700 border border-amber-200",
  "In review": "bg-blue-50 text-blue-700 border border-blue-200",
  Resolved: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Closed: "bg-slate-100 text-slate-700 border border-slate-200",
};
const PRIO_STYLE = {
  Low: "bg-slate-100 text-slate-700",
  Normal: "bg-blue-50 text-blue-700",
  High: "bg-orange-50 text-orange-700",
  Urgent: "bg-rose-50 text-rose-700",
};
const STATUS_ICON = { Open: AlertCircle, "In review": Clock, Resolved: CheckCircle2, Closed: CheckCircle2 };

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [reply, setReply] = useState("");
  const [form, setForm] = useState({ subject: "", category: "", priority: "Normal", body: "" });

  const loadTickets = async () => { try { const response = await apiClient.get("/StudentSupport"); setTickets((response.data || []).map((ticket) => ({ id: ticket.ticketNumber, ticketId: ticket.studentSupportTicketId, subject: ticket.subject, category: ticket.category, priority: ticket.priority, status: ticket.status, created: ticket.createdDate, lastReply: "", unread: false, messages: (ticket.messages || []).map((message) => ({ from: message.isFromStudent ? "you" : "support", body: message.messageBody, at: message.createdDate })) }))); } catch (error) { ToastHelper.apiError(error, "Unable to load support tickets"); } };
  useEffect(() => { loadTickets(); }, []);

  const filtered = tickets.filter((t) => {
    if (filter !== "all" && t.status.toLowerCase().replace(/\s+/g, "-") !== filter) return false;
    if (q && !`${t.id} ${t.subject} ${t.category}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const openCount = tickets.filter((t) => t.status === "Open").length;
  const inReviewCount = tickets.filter((t) => t.status === "In review").length;
  const resolvedCount = tickets.filter((t) => t.status === "Resolved").length;

  const submitTicket = async (e) => {
    e.preventDefault();
    let newTicket; try { const response = await apiClient.post("/StudentSupport", { subject: form.subject, category: form.category, priority: form.priority, messageBody: form.body }); const ticket = response.data; newTicket = { id: ticket.ticketNumber, ticketId: ticket.studentSupportTicketId, subject: ticket.subject, category: ticket.category, priority: ticket.priority, status: ticket.status, created: ticket.createdDate, lastReply: "just now", unread: false, messages: (ticket.messages || []).map((m) => ({ from: m.isFromStudent ? "you" : "support", body: m.messageBody, at: m.createdDate })) }; setTickets((current) => [newTicket, ...current]); } catch (error) { ToastHelper.apiError(error, "Unable to create ticket"); return; }
    setShowNew(false);
    setForm({ subject: "", category: "", priority: "Normal", body: "" });
    ToastHelper.success(`Ticket ${newTicket.id} created`);
    setSelected(newTicket);
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    try { const response = await apiClient.post(`/StudentSupport/${selected.ticketId}/reply`, reply, { headers: { "Content-Type": "application/json" } }); const message = response.data; const updated = { ...selected, messages: [...selected.messages, { from: "you", body: message.messageBody, at: message.createdDate }], lastReply: "just now" };
    setTickets(tickets.map((t) => t.id === selected.id ? updated : t));
    setSelected(updated);
    setReply("");
    ToastHelper.success("Reply sent");
    } catch (error) { ToastHelper.apiError(error, "Unable to send reply"); }
  };

  const closeTicket = async (id) => {
    try { await apiClient.post(`/StudentSupport/${selected.ticketId}/close`);
    setTickets(tickets.map((t) => t.id === id ? { ...t, status: "Closed" } : t));
    if (selected?.id === id) setSelected({ ...selected, status: "Closed" });
    ToastHelper.info(`Ticket ${id} closed`);
    } catch (error) { ToastHelper.apiError(error, "Unable to close ticket"); }
  };

  return (
    <div className="space-y-6" data-testid="support-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-extrabold text-[#0A2540] flex items-center gap-2"><LifeBuoy className="w-6 h-6 text-orange-500" /> Support tickets</h1>
          <p className="text-sm text-slate-500 mt-1">Need a hand? Our team typically replies within 1 business hour, Mon-Sat.</p>
        </div>
        <button onClick={() => setShowNew(true)} className="wc-btn-primary text-sm" data-testid="new-ticket-btn"><Plus className="w-4 h-4" /> New ticket</button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Total" value={tickets.length} tone="slate" />
        <Stat label="Open" value={openCount} tone="amber" />
        <Stat label="In review" value={inReviewCount} tone="blue" />
        <Stat label="Resolved" value={resolvedCount} tone="emerald" />
      </div>

      {/* Filters */}
      <div className="wc-card p-3 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} className="wc-input pl-9" placeholder="Search tickets..." data-testid="support-search" />
        </div>
        <div className="flex flex-wrap gap-1">
          {[["all","All"],["open","Open"],["in-review","In review"],["resolved","Resolved"],["closed","Closed"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${filter === k ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`} data-testid={`support-filter-${k}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Tickets list */}
      <div className="space-y-3" data-testid="tickets-list">
        {filtered.length === 0 && (
          <div className="wc-card py-16 text-center text-slate-500 flex flex-col items-center gap-2">
            <MessageSquare className="w-8 h-8 text-slate-300" />
            <div>No tickets match your search.</div>
          </div>
        )}
        {filtered.map((t) => {
          const StatusIcon = STATUS_ICON[t.status];
          return (
            <button key={t.id} onClick={() => setSelected(t)} className="w-full text-left wc-card flex items-start gap-4 hover:border-orange-300" data-testid={`ticket-${t.id}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${STATUS_STYLE[t.status]}`}><StatusIcon className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-slate-500">{t.id}</span>
                  <span className={`wc-badge ${STATUS_STYLE[t.status]}`}>{t.status}</span>
                  <span className={`wc-badge ${PRIO_STYLE[t.priority]}`}>{t.priority}</span>
                  {t.unread && <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />}
                </div>
                <div className="font-semibold text-[#0A2540] mt-1 truncate">{t.subject}</div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                  <span>{t.category}</span>
                  <span>·</span>
                  <span>{t.messages.length} message{t.messages.length !== 1 ? "s" : ""}</span>
                  <span>·</span>
                  <span>Last reply {t.lastReply}</span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 shrink-0 mt-1" />
            </button>
          );
        })}
      </div>

      {/* New ticket modal */}
      {showNew && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <form onSubmit={submitTicket} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden" data-testid="new-ticket-modal">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div><h2 className="font-display font-extrabold text-[#0A2540] text-lg">New support ticket</h2><div className="text-xs text-slate-500">We'll reply within 1 business hour.</div></div>
              <button type="button" onClick={() => setShowNew(false)} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div><label className="wc-label">Subject</label><input required className="wc-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Briefly describe your issue" data-testid="new-ticket-subject" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="wc-label">Category</label>
                  <select required className="wc-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="new-ticket-category">
                    <option value="">Choose...</option>
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div><label className="wc-label">Priority</label>
                  <select className="wc-input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="new-ticket-priority">
                    {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div><label className="wc-label">Message</label><textarea required rows={5} className="wc-input resize-none" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Tell us what happened, including any error messages or reference numbers..." data-testid="new-ticket-body" /></div>
              <button type="button" className="text-xs font-semibold text-slate-500 hover:text-[#0A2540] inline-flex items-center gap-1"><Paperclip className="w-3.5 h-3.5" /> Attach file (optional)</button>
            </div>
            <div className="p-5 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
              <button type="button" onClick={() => setShowNew(false)} className="wc-btn-outline text-sm">Cancel</button>
              <button type="submit" className="wc-btn-primary text-sm" data-testid="new-ticket-submit"><Send className="w-4 h-4" /> Create ticket</button>
            </div>
          </form>
        </div>
      )}

      {/* Ticket detail drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-stretch justify-end" onClick={() => setSelected(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-xl h-full flex flex-col shadow-2xl" data-testid="ticket-detail">
            <div className="p-5 border-b border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-slate-500">{selected.id}</span>
                  <span className={`wc-badge ${STATUS_STYLE[selected.status]}`}>{selected.status}</span>
                  <span className={`wc-badge ${PRIO_STYLE[selected.priority]}`}>{selected.priority}</span>
                </div>
                <button onClick={() => setSelected(null)} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
              </div>
              <h2 className="font-display font-extrabold text-lg text-[#0A2540]">{selected.subject}</h2>
              <div className="text-xs text-slate-500 mt-1">{selected.category} · Opened {new Date(selected.created).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-slate-50">
              {selected.messages.map((m, i) => (
                <div key={i} className={`flex ${m.from === "you" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl p-3 ${m.from === "you" ? "bg-[#0A2540] text-white rounded-br-sm" : "bg-white border border-slate-200 rounded-bl-sm"}`}>
                    <div className={`text-[10px] uppercase font-semibold tracking-wider mb-1 ${m.from === "you" ? "text-orange-300" : "text-slate-500"}`}>{m.from === "you" ? "You" : (m.agent || "Support")}</div>
                    <div className="text-sm leading-relaxed">{m.body}</div>
                    <div className={`text-[10px] mt-1.5 ${m.from === "you" ? "text-slate-300" : "text-slate-400"}`}>{new Date(m.at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</div>
                  </div>
                </div>
              ))}
            </div>

            {selected.status !== "Closed" ? (
              <div className="p-4 border-t border-slate-200 bg-white">
                <div className="flex gap-2 items-end">
                  <textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply..." className="wc-input resize-none flex-1" data-testid="ticket-reply-input" />
                  <button onClick={sendReply} disabled={!reply.trim()} className="wc-btn-primary text-sm disabled:opacity-40" data-testid="ticket-reply-send"><Send className="w-4 h-4" /></button>
                </div>
                <div className="mt-2 flex justify-between text-xs">
                  <button className="text-slate-500 hover:text-[#0A2540] inline-flex items-center gap-1"><Paperclip className="w-3.5 h-3.5" /> Attach file</button>
                  <button onClick={() => closeTicket(selected.id)} className="font-semibold text-rose-600 hover:underline" data-testid="ticket-close">Close ticket</button>
                </div>
              </div>
            ) : (
              <div className="p-4 border-t border-slate-200 bg-slate-100 text-center text-xs text-slate-500">This ticket is closed. Create a new ticket if you need further help.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const tones = {
    slate: "text-slate-700 bg-slate-100 border-slate-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
    blue: "text-blue-700 bg-blue-50 border-blue-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <div className="text-3xl font-display font-extrabold">{value}</div>
      <div className="text-xs uppercase font-semibold tracking-wide mt-0.5 opacity-80">{label}</div>
    </div>
  );
}
